import { isSameDay } from 'date-fns/isSameDay';
import React, { useEffect, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { Calendar } from '@eika/calendar';
import { Notification } from '@eika/calendar/lib/types';
import { WarningLabel } from '@eika/label';
import { ToggleSwitch } from '@eika/toggle-switch';

import useShowInstantPayment from '../../../../hooks/useShowInstantPayment';
import useTranslation from '../../../../hooks/useTranslation';
import { getWarningMessage } from '../../../shared/DateField/utils';
import CalendarModal from './CalendarModal';

type Props = {
    date: ZonedDate | undefined;
    onChange: (date?: ZonedDate) => void;
    immediate?: boolean;
    onChangeImmediate?: (immediate: boolean) => void;
    error?: string;
    minDate?: ZonedDate;
    calendarState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    useFocusModal?: boolean;
    hideInstant?: boolean;
    setSpanDates?: React.Dispatch<React.SetStateAction<ZonedDate[]>> | undefined;
    bankHolidayDates?: ZonedDate[];
    notification: Notification;
} & React.PropsWithChildren;

const today = getZonedDate();

const DateSelectModal: React.FC<Props> = ({
    children,
    date,
    onChange,
    immediate,
    onChangeImmediate,
    error,
    minDate,
    calendarState,
    useFocusModal = false,
    hideInstant = false,
    setSpanDates,
    bankHolidayDates,
    notification,
}) => {
    const [showCalendar, setShowCalendar] = calendarState;
    const [tempDate, setTempDate] = useState<ZonedDate | undefined>(date);

    useEffect(() => {
        setTempDate(date);
    }, [date]);

    const translated = useTranslation();

    const warningMessage = error || getWarningMessage(notification, tempDate);

    const selectToday = () => setTempDate(minDate ?? today);

    const showInstantPaymentToggle = useShowInstantPayment([!hideInstant, !!onChangeImmediate]);

    return (
        <CalendarModal
            useFocusModal={useFocusModal}
            open={showCalendar}
            setOpen={setShowCalendar}
            footer={
                <Button
                    className="date-field__submit-button"
                    onClick={() => {
                        onChange(tempDate);
                        setShowCalendar(false);
                    }}
                >
                    {translated.continue}
                </Button>
            }
        >
            <div className="date-field__datepicker-container">
                <div>
                    <span className="date-field-strax-payment__container">
                        {showInstantPaymentToggle && onChangeImmediate && (
                            <div className="date-field__immediate-toggle">
                                {translated.immediatePayment.title}
                                <ToggleSwitch checked={immediate ?? false} onChange={onChangeImmediate} />
                            </div>
                        )}
                    </span>
                    {immediate ? (
                        <div className="date-field__immediate-info">
                            <p> {translated.immediatePayment.info} </p>
                            <p> {translated.immediatePayment.priceInfo} </p>
                        </div>
                    ) : (
                        <>
                            <Calendar
                                selected={tempDate}
                                minDate={minDate}
                                onSelect={setTempDate}
                                setSpanDates={setSpanDates}
                                bankHolidayDates={bankHolidayDates}
                            />
                            {tempDate && !isSameDay(tempDate, minDate ?? today) && (
                                <Button
                                    className="date-field__datepicker-today-button"
                                    buttonType="negative"
                                    onClick={selectToday}
                                >
                                    {translated.dateField.today}
                                </Button>
                            )}
                        </>
                    )}
                    {warningMessage && !immediate && (
                        <WarningLabel className="calendar--warning" text={warningMessage} />
                    )}
                </div>
                {children}
            </div>
        </CalendarModal>
    );
};

export default DateSelectModal;
